// Handles all app specific integrations and configs for SSO SDK

import i18n from 'i18next'

import { getWSAuth, WSAuthEnvironment } from '@wonderschool/common-base-sso'

import { BASE_DOMAIN } from 'config'
import { logoutPath } from 'main/routes'

import { getCurrentEnv, LegoEnvType } from './config/env'
import logger from './utils/logger'

export const removeHashAfter: boolean = true

const legoEnvToWsAuthEnv: Record<LegoEnvType, WSAuthEnvironment> = {
  dev: 'local',
  stage: 'dev',
  staging: 'staging',
  prod: 'prod',
}

// Auth-related paths where the wsAuth SDK's sessionNeedsSignout detection
// should follow the original signout flow. On all other pages, suppress
// the entire signout chain (signoutLocal + ssoRedirect) to avoid a race
// condition in the SDK that incorrectly triggers sign-out during
// IDEC/Okta login flows.
//
// The original SDK's WSAuthProvider calls: cleanupForSignout() →
// signoutLocal() (destroys Firebase token) → ssoRedirect() →
// performRedirect(). Patching only performRedirect is insufficient
// because the Firebase token is already destroyed by signoutLocal.
// Instead, we monkey-patch signoutLocal and ssoRedirect on the WSA
// instance to no-op on non-auth pages, matching the behavior of the
// forked SDK used in Nova.
const AUTH_PATHS = ['/login', '/join', '/auth-return', '/logout', '/logout-complete']

function isAuthPath(): boolean {
  const path = window.location.pathname
  return AUTH_PATHS.some(authPath => path === authPath || path.startsWith(authPath + '/'))
}

const WSA = getWSAuth({
  env: legoEnvToWsAuthEnv[getCurrentEnv()],
  isMobileMode: false,
  idTokenJwtMode: false,
  returnUrl: `${BASE_DOMAIN}/auth-return`,
  performRedirect: () => {
    window.location.assign(logoutPath()) // server side logout and redirect to /logout-complete
  },
  getLanguageKey: () => i18n.language,
  loggerTransport: {
    warn: (message, ...context) => logger(message, ...context),
    error: (message, ...context) => logger(message, ...context),
  },
})

// Track the last known good SessionId so we can restore it when we
// suppress the signout chain. The SDK's cleanupForSignout() overwrites
// localStorage "SessionId" to "SigningOut" BEFORE calling signoutLocal(),
// so by the time our patch runs the original value is already gone.
// We capture it proactively: once at module init and on subsequent
// storage events (the SDK's useLocalStorage hook dispatches synthetic
// StorageEvents when the session ID changes).
const SESSION_STORAGE_KEY = 'SessionId'
const SIGNING_OUT_VALUE = '"SigningOut"' // JSON-serialized by the SDK's store2 lib

let lastGoodSessionId: string | null = null

function captureSessionId(): void {
  try {
    const raw = localStorage.getItem(SESSION_STORAGE_KEY)
    if (raw !== null && raw !== SIGNING_OUT_VALUE) {
      lastGoodSessionId = raw
    }
  } catch {
    // Ignore localStorage errors
  }
}

captureSessionId()
window.addEventListener('storage', e => {
  if (e.key === SESSION_STORAGE_KEY && e.newValue && e.newValue !== SIGNING_OUT_VALUE) {
    lastGoodSessionId = e.newValue
  }
})

// Monkey-patch signoutLocal and ssoRedirect to suppress the automatic
// sessionNeedsSignout signout chain on non-auth pages. User-initiated
// logouts go directly to the server /logout endpoint, not through these
// SDK methods, so this only affects the WSAuthProvider's automatic
// signout detection.
const originalSignoutLocal = WSA.Auth.signoutLocal.bind(WSA.Auth)
const originalSsoRedirect = WSA.Auth.ssoRedirect.bind(WSA.Auth)

WSA.Auth.signoutLocal = async () => {
  if (isAuthPath()) {
    return originalSignoutLocal()
  }
  // cleanupForSignout() already ran inside WSAuthProvider before this call,
  // overwriting localStorage "SessionId" to "SigningOut". Restore the
  // previous value so the SDK's useSavedSessionId hook doesn't see a
  // stale locked session on subsequent renders.
  try {
    if (lastGoodSessionId !== null) {
      localStorage.setItem(SESSION_STORAGE_KEY, lastGoodSessionId)
    } else {
      localStorage.removeItem(SESSION_STORAGE_KEY)
    }
  } catch {
    // Ignore localStorage errors
  }
  logger('wsAuth signoutLocal suppressed on', window.location.pathname)
  return true
}

WSA.Auth.ssoRedirect = (opts = {}) => {
  if (isAuthPath()) {
    return originalSsoRedirect(opts)
  }
  logger('wsAuth ssoRedirect suppressed on', window.location.pathname)
}

export default WSA
